import { observer } from "mobx-react";
import {
  addFreetextActions,
  addFreetextState,
  addFreetextTypeOptions,
  DbotEventDispatcher,
  decisionSelectorState,
  DocumentContext,
  openCopyDecisionSelector,
  ProjectContext,
  ProjectControllerState,
  selectedFreetextTypeOption,
  supportsAttachments,
} from "./commonDomain";
import { Button } from "../../components-v2/Button";

import { useTranslation } from "../../stores/RootStoreContext";
import { DropdownMenu } from "../../components-v2/Dropdown";
import { TextInput } from "../../components-v2/TextInput";
import { isEnter, isEscape } from "../../utils/keyboardEvents";
import { Box, VBox } from "../../components-v2/Box";
import { when } from "../../utils/functions";
import { SelectFilesButton } from "../../components-v2/SelectFilesButton";
import { DocumentData } from "../project/projectDomain";
import { Select } from "../../components-v2/Select";
import { Tag } from "../../components-v2/Tag";

export const AddAgendaItemButton = observer(function AddAgendaItemButton({
  documentId,
  state,
  dispatch,
  projectId,
}: {
  documentId?: string;
  state: ProjectControllerState;
  dispatch: DbotEventDispatcher;
  projectId: string;
}) {
  const { t } = useTranslation();
  switch (state.type) {
    case "add-freetext": {
      const actions = addFreetextActions(state);
      return (
        <VBox grow>
          <Box>
            <Box>
              <Select
                items={addFreetextTypeOptions}
                onSelect={(option) => dispatch(actions.selectFreetextType(option))}
                idFn={(option) => option.value}
                labelFn={(option) => option.label}
                iconFn={(option) => option.icon}
                value={selectedFreetextTypeOption(state)}
                iconOnly
              />
            </Box>
            <TextInput
              autofocus
              placeholder={t("dbot.add.freetext-placeholder")}
              value={state.input}
              onChange={(event) => dispatch(actions.onChange(event))}
              onKeyDown={(event) => {
                switch (true) {
                  case isEnter(event) && actions.submit !== undefined:
                    dispatch(actions.submit);
                    break;
                  case isEscape(event):
                    dispatch(actions.cancel);
                    break;
                }
              }}
              readOnly={state.isLoading}
            />
          </Box>

          <Box justifyContent={"end"}>
            <Button
              variant={"primary"}
              label={t("create")}
              disabled={!actions.submit}
              onClick={() => when(actions.submit, dispatch)}
              loading={state.isLoading}
            />
            <Button
              variant={"secondary"}
              label={t("cancel")}
              onClick={() => dispatch(actions.cancel)}
            />
          </Box>
        </VBox>
      );
    }

    default:
      return (
        <div>
          <DropdownMenu
            button={{ type: "link", icon: "plus", label: t("dbot.add.agenda") }}
            items={[
              {
                type: "item",
                icon: "text-lines",
                label: t("decisionType.freetext.name"),
                onSelect: () =>
                  dispatch({ type: "dbot/change-state", state: addFreetextState({ documentId }) }),
              },
              {
                type: "item",
                icon: "sparkle",
                label: t("dbot.select"),
                onSelect: () => {
                  dispatch({
                    type: "dbot/change-state",
                    state: decisionSelectorState({ documentId }),
                  });
                },
              },
              {
                type: "item",
                icon: "copy",
                label: (
                  <Box>
                    {t("dbot.duplicate.decision")} <Tag label={t("new")} color={"info"} />
                  </Box>
                ),
                onSelect: () => {
                  dispatch(openCopyDecisionSelector(projectId, documentId));
                },
              },
            ]}
          />
        </div>
      );
  }
});

export const AgendaActions = observer(
  ({
    isLocked,
    context,
    uploadAttachments,
    dispatch,
  }: {
    isLocked: boolean;
    context: ProjectContext | DocumentContext<DocumentData>;
    uploadAttachments?: (files: File[]) => void;
    dispatch: DbotEventDispatcher;
  }) => {
    const { t } = useTranslation();
    return isLocked ? null : (
      <Box alignItems={"center"} justifyContent={"space-between"}>
        <AddAgendaItemButton
          state={context.state}
          dispatch={dispatch}
          documentId={"documentId" in context ? context.documentId : undefined}
          projectId={context.projectId}
        />
        {uploadAttachments &&
          supportsAttachments(context) &&
          context.state.type !== "add-freetext" && (
            <SelectFilesButton
              fileUploadConfig={context.fileUploadConfig}
              onChange={uploadAttachments}
              variant={"link"}
              icon={"upload"}
              label={t("upload-attachments")}
            />
          )}
      </Box>
    );
  },
);
